import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';

interface IResponseOption {
  value: string;
  label: string;
}

interface IAdvancementAttendanceData {
  ProjectGuid: string;
  ProjectIdInt: number;
  EntryTitle: string;
  EventName: string;
  ParticipantNames: string[];
  CurrentResponse: string | null;
  CurrentResponseLabel: string;
  RespondedAtUtc?: string;
  ResponseOptions: IResponseOption[];
}

const AdvancementAttendanceResponsePage = () => {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const [data, setData] = useState<IAdvancementAttendanceData | null>(null);
  const [selectedResponse, setSelectedResponse] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const projectId = query.get('projectId') || '';
  const responseFromLink = query.get('response') || '';

  useEffect(() => {
    if (!projectId) {
      toast.error('Missing project identifier in link.');
      return;
    }

    store.server
      .postApi<ServerResponse<IAdvancementAttendanceData>>(
        `../AdvancementAttendance/Data?projectId=${encodeURIComponent(projectId)}`,
        {}
      )
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
          setSelectedResponse(x.Value.CurrentResponse || '');

          if (responseFromLink) {
            submitResponse(x.Value.ProjectGuid, responseFromLink);
          }
        } else {
          toast.error(x.Message || 'Unable to load attendance response details.');
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Unable to load attendance response details.');
      });
  }, [projectId, responseFromLink]);

  const submitResponse = (projectGuid: string, responseValue: string) => {
    setIsSaving(true);
    store.server
      .postApi<ServerResponse<any>>('../AdvancementAttendance/Save', {
        ProjectId: projectGuid,
        Response: responseValue,
      })
      .then((x) => {
        if (x.Success) {
          toast.success('Response updated.');
          setSelectedResponse(responseValue);
          setData((prev) =>
            prev
              ? {
                  ...prev,
                  CurrentResponse: x.Value.Response,
                  CurrentResponseLabel: x.Value.ResponseLabel,
                  RespondedAtUtc: x.Value.RespondedAtUtc,
                }
              : prev
          );
        } else {
          toast.error(x.Message || 'Unable to save response.');
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Unable to save response.');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const saveResponse = () => {
    if (!data) {
      return;
    }

    if (!selectedResponse) {
      toast.warning('Please select a response first.');
      return;
    }

    submitResponse(data.ProjectGuid, selectedResponse);
  };

  if (!data) {
    return (
      <div className="main-body-page grow">
        <h2>Advancement Attendance Response</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="main-body-page grow advancement-attendance-container">
      <h2>Advancement Attendance Response</h2>
      <p>Thank you for letting us know your attendance plans for {data.EventName}.</p>

      <div className="soft-border advancement-attendance-box">
        <div>
          <strong>Entry:</strong> {data.EntryTitle || 'Untitled entry'}
        </div>
        <div>
          <strong>Participant(s):</strong> {data.ParticipantNames?.join(', ') || 'N/A'}
        </div>
        <div>
          <strong>Current response:</strong> {data.CurrentResponseLabel || 'Not selected'}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="advancement-response-select">Update your response</label>
        <select
          id="advancement-response-select"
          className="form-control"
          value={selectedResponse}
          onChange={(e) => setSelectedResponse(e.target.value)}>
          <option value="">Select your response</option>
          {data.ResponseOptions.map((x) => (
            <option key={x.value} value={x.value}>
              {x.label}
            </option>
          ))}
        </select>
      </div>

      <div className="advancement-attendance-actions">
        <button className="btn btn-primary" onClick={saveResponse} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Response'}
        </button>
      </div>
    </div>
  );
};

export default AdvancementAttendanceResponsePage;
